/* eslint-disable react/require-default-props */
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import {
  Image,
  Link,
  mergeClasses,
  Text,
  Title1,
  tokens,
} from '@fluentui/react-components';

import { useIdpCenteredSelectionPageStyles } from './use-idp-centered-selection-page-styles';
import { IdpButtonList, type IdpButtonListProps } from './idp-button-list';
import { IdpMarketingImage } from './idp-marketing-image';
import { TroubleshootDrawer } from './troubleshoot-drawer';
import { UserInvitationSelectionPage } from './accept-invitation/user-invitation-selection-page';

import MicrosoftAdvertisingLogo from '../../../media/microsoft-advertising-logo-idp-header.png';

export type IdpCenteredSelectionPageProps = {
  scenario: 'signup' | 'signin' | 'userInvitation';
  isAcceptInvitationClientSideFlow?: boolean;
  logger?: {
    // eslint-disable-next-line no-unused-vars
    trace: (message: string) => void;
  };
};

export const IdpCenteredSelectionPage: React.FC<IdpCenteredSelectionPageProps> = ({ scenario, isAcceptInvitationClientSideFlow = false, logger }) => {
  const i18n = useContext(I18nContext) as any;
  const classes = useIdpCenteredSelectionPageStyles();
  const serverContext = useContext(ServerContext) as any;
  const isSignInTroubleShootingEnabled = serverContext?.Permissions?.isSignInTroubleShootingEnabled;
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { SignUpIdentitySelection, SignInIdentitySelection } = serverContext?.Urls || {};

  const providers: IdpButtonListProps['providers'] = useMemo(() => {
    if (scenario === 'signup') {
      logger?.trace(`IdpCenteredSelectionPage: isGoogleTopIdentityProviderEnabled=${serverContext?.Permissions?.isGoogleTopIdentityProviderEnabled}`);
      if (serverContext?.Permissions?.isGoogleTopIdentityProviderEnabled) {
        return ['Google', 'MSA', 'Facebook'];
      }
    }
    return ['MSA', 'Google', 'Facebook'];
  }, [logger, scenario, serverContext?.Permissions?.isGoogleTopIdentityProviderEnabled]);

  const titleText = useMemo(() => {
    if (scenario === 'signup') {
      return i18n.getString(_TL_('Join the Microsoft Advertising Platform'));
    } else if (scenario === 'signin') {
      return i18n.getString(_TL_('Welcome back'));
    }
    return i18n.getString(_TL_('Accept your invitation to Microsoft Advertising'));
  }, [i18n, scenario]);

  const descriptionText = useMemo(() => {
    if (scenario === 'signup') {
      return i18n.getString(_TL_('Unlock our cutting edge tools, which are designed to help you grow your business.'));
    } else if (scenario === 'signin') {
      return null;
    }
    return i18n.getString(_TL_('You\'ve been invited to an account.'));
  }, [i18n, scenario]);

  const onSignInLinkClick = useCallback(() => {
    logger?.trace(`IdpCenteredSelectionPage: Navigating to signin page: ${SignInIdentitySelection}`);
    window.open(SignInIdentitySelection, '_self');
  }, [logger, SignInIdentitySelection]);

  const onCreateNewAccountLinkClick = useCallback(() => {
    logger?.trace(`IdpCenteredSelectionPage: Navigating to signup page: ${SignUpIdentitySelection}`);
    window.open(SignUpIdentitySelection, '_self');
  }, [logger, SignUpIdentitySelection]);

  // @aiContributed-MAPFuse-2026-04-22
  const onCantAccessAccountLinkClick = useCallback(() => {
    logger?.trace('IdpCenteredSelectionPage: User clicked "Can\'t access your account?" link');
  }, [logger]);

  if (scenario === 'userInvitation' && isAcceptInvitationClientSideFlow) {
    return <UserInvitationSelectionPage logger={logger} />;
  }

  return (
    <div className={mergeClasses('idp-centered-selection-page', classes.idpSelectionPage)}>
      <div className={classes.content}>
        <section className={mergeClasses('identity-provider-selection-section', classes.section)}>
          <div className={classes.contentLeft}>
            <Image
              src={MicrosoftAdvertisingLogo}
              alt={i18n.getString(_TL_('Microsoft Advertising'))}
              width={157}
              height={24}
            />

            <div className={classes.textAndButtons}>
              <Title1
                block
                className={mergeClasses('idp-title', classes.idpSelectionPageTitle)}
              >
                {titleText}
              </Title1>

              {descriptionText && (
                <Text
                  block
                  size={400}
                  color={tokens.colorNeutralForeground2}
                >
                  {descriptionText}
                </Text>
              )}

              <div className={mergeClasses('idp-selection-page-buttons', classes.buttons)}>
                <IdpButtonList scenario={scenario} logger={logger} providers={providers} />
              </div>

              {scenario === 'signin' && (
                <div className={classes.buttonsFooter}>
                  <Link
                    className="cant-access-account-link"
                    {...(isSignInTroubleShootingEnabled
                      ? {
                        onClick: () => {
                          onCantAccessAccountLinkClick();
                          logger?.trace('IdpCenteredSelectionPage: Opening troubleshoot drawer');
                          setIsDrawerOpen(true);
                        },
                        'aria-haspopup': 'dialog' as const,
                        'aria-expanded': isDrawerOpen,
                      }
                      : {
                        href: 'https://help.ads.microsoft.com/#apex/ads/en/60259/0',
                        target: '_blank',
                        rel: 'noopener noreferrer',
                        onClick: onCantAccessAccountLinkClick,
                      }
                    )}
                  >
                    {i18n.getString(_TL_('Can\'t access your account?'))}
                  </Link>
                </div>
              )}
            </div>
          </div>

          {scenario === 'userInvitation' && (
            <div className={mergeClasses(classes.contentRight, 'ad-preview-section')}>
              <IdpMarketingImage />
            </div>
          )}
        </section>

        {/* @aiContributed-MAPFuse-2026-03-25 */}
        {scenario === 'signup' && (
          <section className={mergeClasses(classes.section, classes.contentLeft, classes.sectionAdditionalInfo)}>
            <Text weight="semibold">{i18n.getString(_TL_('Already have an account?'))}</Text>
            {' '}
            <Link className="sign-in-link" onClick={onSignInLinkClick}>{i18n.getString(_TL_('Sign in'))}</Link>
          </section>
        )}

        {scenario === 'signin' && (
          <section className={mergeClasses(classes.section, classes.contentLeft, classes.sectionAdditionalInfo)}>
            {i18n.getString(_TL_('No account?'))}
            {' '}
            <Link className="create-new-account-link" onClick={onCreateNewAccountLinkClick}>{i18n.getString(_TL_('Create new account'))}</Link>
          </section>
        )}
      </div>

      {scenario === 'signin' && isSignInTroubleShootingEnabled && (
        <TroubleshootDrawer open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} logger={logger} />
      )}
    </div>
  );
};
